import { useCallback, useEffect, useState } from 'react';
import { request } from 'graphql-request';
import { Text, Box, Image, WrapItem, HStack, VStack, Stack, Flex, Wrap } from '@chakra-ui/react';
import { RichText } from '@graphcms/rich-text-react-renderer';
import { useLocation, useNavigate } from 'react-router-dom';
import { WindowFrame, useWindowBreakpoints } from '../../../components/window';
import {
  buildWindowsPath,
  closePostInList,
  openPostInList,
  parseOpenSegments,
  zIndexForSegment,
} from '../../../routing/windows';
import { calculateTimeNow } from '../blogs/parts/blogDate';
import { BlogContentImage } from '../blogs/parts/BlogContentImage';
import {
  BLOG_POSTS_QUERY,
  GRAPHCMS_ENDPOINT,
  type BlogPost,
  type PostsQueryResult,
} from '../blogs/parts/blogTypes';
import RotatingCubeLoader from '../../../components/RotatingCubeLoader';
import '../blogs/styles.css';

interface BlogPostWindowProps {
  slug: string;
}

/**
 * Post body restored to the previous visual design (grid hero, title scale, spacing),
 * while still scaling padding/type slightly from OS window width.
 */
function BlogPostBody({ post }: { post: BlogPost }) {
  const { isXs, isSm } = useWindowBreakpoints();

  // Match original Chakra-scale spacing (theme space units ≈ 0.25rem each),
  // scaled down only in narrow OS windows.
  const padX = isXs ? 20 : isSm ? 32 : 40; // was paddingInline={40}
  const padTop = isXs ? 28 : isSm ? 36 : 40; // was paddingTop={40}
  const padBottom = isXs ? 64 : isSm ? 96 : 120; // was paddingBottom={120}
  const titleSize = isXs ? '1.5rem' : isSm ? '1.75rem' : '2.5rem'; // was [1rem,1rem,1.75rem,2.5rem]
  const titleLineHeight = isXs ? '2rem' : isSm ? '2.25rem' : '3rem';
  const titlePadBottom = isXs ? 20 : isSm ? 28 : 40; // was paddingBottom={[0,40]}
  const titlePadX = isXs ? 12 : isSm ? 32 : 80; // was paddingInline={[80]}
  const descPadY = isXs ? 16 : 20; // was paddingBlock={20}
  const descPadX = isXs ? 12 : isSm ? 32 : 80; // was paddingInline={80}
  const bodySize = isXs ? '1.1rem' : isSm ? '1.2rem' : '1.35rem';
  const contentMaxW = isSm ? '90%' : '60%'; // was w={['80%','80%','60%','60%']}
  const metaMaxW = isSm ? '100%' : '90%'; // was Flex w="90%"
  const heroImageW = isXs ? '92%' : '75%';
  const readTimePadX = isXs ? 12 : isSm ? 32 : 80;
  const sectionGap = isXs ? 20 : 30; // was mb={30} on header row

  return (
    <Box
      w="100%"
      h="100%"
      bg="#0F1117"
      color="white"
      overflowY="auto"
      overflowX="hidden"
      className="blog-post-window-body"
      sx={{
        '::-webkit-scrollbar': {
          display: 'none',
        },
      }}
    >
      {/*
        Original layout used a ~400px-tall grid hero with the same layered
        gradients; content sits above it and continues on solid #0F1117.
      */}
      <Box position="relative" w="100%" minH="100%">
        <Box
          className="blog-post-window-grid"
          position="absolute"
          top={0}
          left={0}
          right={0}
          height="400px"
          pointerEvents="none"
          zIndex={0}
          background="linear-gradient(to bottom, rgba(15, 17, 23, 1), rgba(15, 17, 23, 0.5)), repeating-linear-gradient(0deg, #555, #555 1px, transparent 1px, transparent 30px), repeating-linear-gradient(90deg, #555, #555 1px, transparent 1px, transparent 30px)"
          _after={{
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(15, 17, 23, 0), rgba(15, 17, 23, 1))',
          }}
        />

        <Box
          position="relative"
          zIndex={2}
          textAlign="center"
          w="100%"
          maxW="100%"
          boxSizing="border-box"
          paddingTop={`${padTop}px`}
          paddingBottom={`${padBottom}px`}
          paddingInline={`${padX}px`}
        >
          {/* Read time — original top-right treatment */}
          <HStack
            w="100%"
            align="center"
            justifyContent="flex-end"
            mb={`${sectionGap}px`}
            minH="32px"
          >
            <Text
              fontSize="0.95rem"
              fontWeight="normal"
              fontFamily="Roboto, sans-serif"
              lineHeight="1.25rem"
              color="#9FA8C1"
              m={0}
              paddingInline={`${readTimePadX}px`}
              whiteSpace="nowrap"
            >
              {post.readTime} min read
            </Text>
          </HStack>

          {/* Title — original size / weight / line-height */}
          <Text
            fontSize={titleSize}
            fontWeight="bold"
            fontFamily="Roboto, sans-serif"
            lineHeight={titleLineHeight}
            color="#e9ebf1"
            textAlign="center"
            m={0}
            paddingBottom={`${titlePadBottom}px`}
            paddingInline={`${titlePadX}px`}
          >
            {post.title}
          </Text>

          <Image
            objectFit="cover"
            borderRadius="6px"
            src={post.coverPhoto.url}
            w={heroImageW}
            maxW="100%"
            h="auto"
            mx="auto"
            display="block"
            alt=""
            mb={`${descPadY}px`}
          />

          <Text
            fontSize="1rem"
            fontWeight="normal"
            fontFamily="Roboto, sans-serif"
            lineHeight="1.25rem"
            color="#9FA8C1"
            m={0}
            paddingTop={`${descPadY}px`}
            paddingBottom={`${descPadY}px`}
            paddingInline={`${descPadX}px`}
          >
            {post.description}
          </Text>

          {/* Tags + published date — more horizontal/vertical room */}
          <Flex
            w={metaMaxW}
            maxW="100%"
            mx="auto"
            mt={`${descPadY}px`}
            mb={`${descPadY}px`}
            align="center"
            justifyContent="space-between"
            flexDirection={isXs ? 'column' : 'row'}
            gap={`${isXs ? 16 : 24}px`}
            px={isXs ? 0 : 2}
          >
            <Wrap spacing={`${isXs ? 12 : 16}px`} justify={isXs ? 'center' : 'flex-start'}>
              {post.tags.map((data, index) => (
                <WrapItem key={index}>
                  <Text
                    m={0}
                    border="1px solid #222222"
                    borderRadius="6px"
                    fontSize="0.95rem"
                    fontFamily="Roboto, sans-serif"
                    color="#e9ebf1"
                    lineHeight="1.25rem"
                    px="20px"
                    py="10px"
                  >
                    {data}
                  </Text>
                </WrapItem>
              ))}
            </Wrap>
            <Text
              fontSize="0.9rem"
              fontWeight="normal"
              fontFamily="Roboto, sans-serif"
              lineHeight="1.25rem"
              color="#9FA8C1"
              m={0}
              whiteSpace="nowrap"
              flexShrink={0}
            >
              Published{' '}
              <Text as="span" color="#e9ebf1" paddingStart={2}>
                {calculateTimeNow(post.datePublished)}
              </Text>
            </Text>
          </Flex>

          <VStack
            className="blog-post-rich-text"
            pt={`${isXs ? 32 : 40}px`}
            w={contentMaxW}
            maxW="100%"
            mx="auto"
            spacing={`${isXs ? 16 : 20}px`}
            align="start"
            textAlign="start"
          >
            <RichText
              content={post.content.raw}
              references={post.content.references}
              renderers={{
                h1: ({ children }) => (
                  <Text
                    fontSize={isSm ? '1.75rem' : '2.5rem'}
                    fontWeight="bold"
                    fontFamily="Roboto, sans-serif"
                    lineHeight={isSm ? '2.25rem' : '3rem'}
                    color="#e9ebf1"
                    paddingBlock={`${isXs ? 12 : 20}px`}
                    m={0}
                  >
                    {children}
                  </Text>
                ),
                h2: ({ children }) => (
                  <Text
                    fontSize={isSm ? '1.5rem' : '2rem'}
                    fontWeight="bold"
                    fontFamily="Roboto, sans-serif"
                    lineHeight={isSm ? '2rem' : '3rem'}
                    color="#e9ebf1"
                    paddingBlock={`${isXs ? 12 : 20}px`}
                    m={0}
                  >
                    {children}
                  </Text>
                ),
                p: ({ children }) => (
                  <Text
                    fontSize={bodySize}
                    fontWeight="normal"
                    fontFamily="Roboto, sans-serif"
                    color="#dadde7"
                    lineHeight={isSm ? '2rem' : '2.5rem'}
                    m={0}
                    w="100%"
                  >
                    {children}
                  </Text>
                ),
                a: ({ children, href, openInNewTab }) => (
                  <a
                    href={href}
                    target={openInNewTab ? '_blank' : '_self'}
                    style={{ color: 'green' }}
                    rel="noreferrer"
                  >
                    {children}
                  </a>
                ),
                bold: ({ children }) => <strong>{children}</strong>,
                // Inline / block images from the rich text AST
                img: (props) => <BlogContentImage {...props} />,
                // Embedded CMS assets (typical for Hygraph image embeds)
                Asset: {
                  image: (props) => <BlogContentImage {...props} />,
                  text: () => (
                    <div>
                      <p>text plain</p>
                    </div>
                  ),
                },
              }}
            />
          </VStack>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Desktop window for a single blog post.
 * Title bar shows the post slug; URL segment is the same slug.
 */
export function BlogPostWindow({ slug }: BlogPostWindowProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState<BlogPost | null | undefined>(undefined);

  const zIndex = zIndexForSegment(location.pathname, { kind: 'post', slug });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        // Use the same simple list query as the Blogs menu (reliable for this CMS).
        const all = await request<PostsQueryResult>(GRAPHCMS_ENDPOINT, BLOG_POSTS_QUERY);
        if (cancelled) return;
        const found = all.posts.find((p) => p.slug.toLowerCase() === slug.toLowerCase());
        setPost(found ?? null);
      } catch {
        if (!cancelled) {
          setPost(null);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleClose = useCallback(() => {
    const next = closePostInList(parseOpenSegments(location.pathname), slug);
    navigate(buildWindowsPath(next));
  }, [location.pathname, navigate, slug]);

  const handleFocus = useCallback(() => {
    const current = parseOpenSegments(location.pathname);
    const next = openPostInList(current, slug);
    const path = buildWindowsPath(next);
    if (path !== location.pathname) {
      navigate(path);
    }
  }, [location.pathname, navigate, slug]);

  return (
    <WindowFrame
      title={slug}
      windowId={`blog_post_${slug}`}
      zIndex={zIndex}
      theme="dark"
      sizePreset="lg"
      onClose={handleClose}
      onFocus={handleFocus}
      contentClassName="blogs-window-content"
    >
      {post === undefined ? (
        <Box w="100%" h="100%" bg="#0F1117">
          <RotatingCubeLoader />
        </Box>
      ) : post === null ? (
        <Stack align="center" justifyContent="center" minH="40%" h="100%" spacing={4} bg="#0F1117">
          <Text fontFamily="Roboto, sans-serif" color="#9fa8c1">
            Post not found
          </Text>
        </Stack>
      ) : (
        <BlogPostBody post={post} />
      )}
    </WindowFrame>
  );
}

export default BlogPostWindow;
